import './TreeView.scss';
import { useMemo, type ReactNode } from 'react';
import { Spinner } from 'react-bootstrap';
import Chevron from 'components/Chevron/Chevron';

type Id = string | number;

export interface TreeViewProps<T, K extends Id = Id> {
  /** Top-level nodes. */
  readonly roots: readonly T[];
  readonly getId: (node: T) => K;
  readonly getChildren: (node: T) => readonly T[];
  /** Renders a node's label (text, badges, match highlighting — all caller-owned). */
  readonly renderLabel: (node: T) => ReactNode;
  /** Ids of expanded branches. */
  readonly expandedIds: ReadonlySet<K>;
  readonly onToggle: (id: K) => void;
  readonly selectedId?: K;
  readonly onSelect?: (node: T) => void;
  /**
   * Whether a node shows a chevron. Defaults to "has loaded children"; override
   * to mark a node expandable before its children are loaded (lazy loading).
   */
  readonly hasChildren?: (node: T) => boolean;
  /** Whether a node is currently loading its children (shows a spinner in place of the chevron). */
  readonly isLoading?: (node: T) => boolean;
  readonly className?: string;
}

/** Pixels of left padding added per depth level. */
const INDENT_PX = 16;

interface TreeNodeConfig<T, K extends Id = Id> {
  readonly getId: (node: T) => K;
  readonly getChildren: (node: T) => readonly T[];
  readonly renderLabel: (node: T) => ReactNode;
  readonly expandedIds: ReadonlySet<K>;
  readonly onToggle: (id: K) => void;
  readonly selectedId?: K;
  readonly onSelect?: (node: T) => void;
  readonly hasChildren: (node: T) => boolean;
  readonly isLoading?: (node: T) => boolean;
}

function TreeNode<T, K extends Id = Id>({ node, depth, config }: { readonly node: T; readonly depth: number; readonly config: TreeNodeConfig<T, K> }) {
  const id = config.getId(node);
  const expandable = config.hasChildren(node);
  const expanded = config.expandedIds.has(id);
  const loading = config.isLoading?.(node) ?? false;

  return (
    <>
      <div
        className={`tree-node-row${id === config.selectedId ? ' tree-node-row-active' : ''}`}
        style={{ paddingLeft: depth * INDENT_PX }}
        onClick={() => config.onSelect?.(node)}
      >
        {loading ? (
          <span className="tree-node-toggle">
            <Spinner animation="border" size="sm" />
          </span>
        ) : expandable ? (
          <Chevron expanded={expanded} onToggle={() => config.onToggle(id)} className="tree-node-toggle" />
        ) : (
          <span className="tree-node-toggle tree-node-toggle-empty" />
        )}
        <span className="tree-node-label">{config.renderLabel(node)}</span>
      </div>
      {expanded && config.getChildren(node).map((child) => <TreeNode key={config.getId(child)} node={child} depth={depth + 1} config={config} />)}
    </>
  );
}

/**
 * A generic, plain-recursive expand/collapse tree. A pure controlled view: the
 * caller (typically a MobX model) owns expansion, selection, and any filtering
 * (by pruning `roots`/`getChildren` and force-expanding match paths).
 */
export function TreeView<T, K extends Id = Id>({
  roots,
  getId,
  getChildren,
  renderLabel,
  expandedIds,
  onToggle,
  selectedId,
  onSelect,
  hasChildren,
  isLoading,
  className,
}: TreeViewProps<T, K>) {
  const config = useMemo<TreeNodeConfig<T, K>>(
    () => ({
      getId,
      getChildren,
      renderLabel,
      expandedIds,
      onToggle,
      selectedId,
      onSelect,
      hasChildren: hasChildren ?? ((node) => getChildren(node).length > 0),
      isLoading,
    }),
    [getId, getChildren, renderLabel, expandedIds, onToggle, selectedId, onSelect, hasChildren, isLoading]
  );

  return (
    <div className={`tree-view${className ? ` ${className}` : ''}`}>
      {roots.map((root) => (
        <TreeNode key={getId(root)} node={root} depth={0} config={config} />
      ))}
    </div>
  );
}

export default TreeView;
